import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { Link } from "react-router-dom";
import "./FeaturedBusinessBanner.css";

// ... (Keep your Star and StarRating components exactly as they are) ...
const Star = ({ variant = "empty", id, size = 16 }) => {
  const starPath =
    "M12 .587l3.668 7.568L24 9.423l-6 5.852 1.417 8.261L12 18.897 4.583 23.536 6 14.275 0 9.423l8.332-1.268L12 .587z";
  const halfClipId = `fbb-clip-${id}`;
  if (variant === "half") {
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} className="fbb-star" aria-hidden="true">
        <defs><clipPath id={halfClipId}><rect x="0" y="0" width="12" height="24" /></clipPath></defs>
        <path d={starPath} className="fbb-star-empty" />
        <path d={starPath} className="fbb-star-filled" clipPath={`url(#${halfClipId})`} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="fbb-star" aria-hidden="true">
      <path d={starPath} className={variant === "filled" ? "fbb-star-filled" : "fbb-star-empty"} />
    </svg>
  );
};

const StarRating = ({ rating = 0 }) => {
  const uidRef = useRef(Math.random().toString(36).slice(2));
  const full = Math.floor(Number(rating) || 0);
  const hasHalf = (Number(rating) || 0) - full >= 0.5;
  return (
    <div className="fbb-stars">
      {Array.from({ length: 5 }).map((_, i) => {
        const idx = i + 1;
        let variant = "empty";
        if (idx <= full) variant = "filled";
        else if (idx === full + 1 && hasHalf) variant = "half";
        return <Star key={`${uidRef.current}-${idx}`} id={`${uidRef.current}-${idx}`} variant={variant} size={14} />;
      })}
    </div>
  );
};

// ... (Keep toAbsoluteImageUrl and normalizeBusiness exactly as they are) ...
function toAbsoluteImageUrl(url) {
  if (!url || typeof url !== "string") return "/img/img-logo.png";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;
  const maybeBase64 = /^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 100 && !/^https?:\/\//i.test(trimmed);
  if (maybeBase64) return `data:image/jpeg;base64,${trimmed}`;
  try { const test = new URL(trimmed); return test.href; } catch (e) { }
  if (trimmed.startsWith("/")) return `${API_BASE_URL}${trimmed}`;
  return trimmed;
}

const normalizeBusiness = (b) => {
  const id = b.businessId || b.businessUserId || b.id;
  const name = b.businessName || b.name || "Business";
  const postcode = b.postCode || b.postcode || "";
  const rawImage = b.profileImage || b.profilePic || b.image || null;
  const image = toAbsoluteImageUrl(rawImage);
  const rawCover = b.CoverImage || b.coverImage || null;
  const coverImage = rawCover ? toAbsoluteImageUrl(rawCover) : null;
  const rating = b.overallRating ? parseFloat(b.overallRating) : 0;
  const suburb = b.suburb || "";

  return {
    id, name, postcode, image, rating, suburb,
    description: b.description || b.profileDescription || "No description available.",
    email: b.businessEmail,
    phone: b.businessPhone,
    website: b.website,
    location: suburb && postcode ? `${suburb}, ${postcode}` : suburb || postcode || "Australia",
    logo: image,
    coverImage: coverImage
  };
};

const FeaturedBusinessBanner = ({ onBusinessClick }) => {
  const [businesses, setBusinesses] = useState([]);

  const pausedRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const animationFrameRef = useRef(null);

  // SAFARI FIX: We must track position in a JS variable, not rely on DOM scrollLeft reading
  const scrollPosRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Business/`);
        if (!isMounted) return;
        const list = Array.isArray(response.data) ? response.data : [];
        const normalized = list.map(normalizeBusiness).filter((x) => x.id !== null);
        setBusinesses(normalized);
      } catch (e) {
        setBusinesses([]);
      }
    };
    fetchFeatured();
    return () => { isMounted = false; };
  }, []);

  const trackItems = useMemo(() => {
    if (!businesses || businesses.length === 0) return [];
    return [...businesses, ...businesses, ...businesses, ...businesses];
  }, [businesses]);

  // --- UNIVERSAL SCROLLING SCRIPT (SAFARI COMPATIBLE) ---
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || trackItems.length === 0) return;

    // Reset position logic on data load
    scrollPosRef.current = container.scrollLeft;

    const scrollSpeed = 0.5;

    const animate = () => {
      if (!pausedRef.current && container) {
        // 1. Update the JS float variable
        scrollPosRef.current += scrollSpeed;

        // 2. Check boundaries based on our JS variable
        const singleSetWidth = container.scrollWidth / 4;

        if (scrollPosRef.current >= singleSetWidth) {
          // Snap back to 0 seamlessly
          scrollPosRef.current = 0;
          container.scrollLeft = 0;
        } else {
          // 3. Apply the integer value to the DOM
          container.scrollLeft = scrollPosRef.current;
        }
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [trackItems]);

  const handleMouseEnter = () => { pausedRef.current = true; };
  const handleMouseLeave = () => { pausedRef.current = false; };

  const handleCardClick = (e, biz) => {
    e.preventDefault();
    if (onBusinessClick) onBusinessClick(biz);
  };

  if (!businesses || businesses.length === 0) {
    return (
      <section className="fbb-section">
        <div className="fbb-container fbb-empty">
          <div className="fbb-empty-text">Loading Featured Businesses...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="fbb-section">
      <Link to="/sponsors" className="fbb-header-link" title="View all Sponsors">
        <span className="fbb-header-text">
          Our Sponsors and Partners
          <span className="fbb-header-icon">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </span>
      </Link>

      <div
        className="fbb-container"
        ref={scrollContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
        // SAFARI FIX: Ensure overflow is 'auto' or 'scroll' so Safari knows it CAN move,
        // but use CSS to hide the bar.
        style={{ overflowX: 'auto' }}
      >
        <div className="fbb-track fbb-track-tight">
          {trackItems.map((biz, idx) => {
            const addr = biz.location || "Featured Business";
            return (
              <a
                key={`${biz.id ?? "x"}-${idx}`}
                href={`/business/${biz.id ?? ""}`}
                className="fbb-card fbb-card-flat"
                onClick={(e) => handleCardClick(e, biz)}
                aria-label={biz.name} getFeaturedBusinesses
                title={biz.name}
              >
                <img src={biz.image} alt={biz.name} className="fbb-image" loading="lazy" draggable="false" />
                <div className="fbb-overlay" />
                <div className="fbb-details">
                  <h3 className="fbb-name">{biz.name}</h3>
                  {addr ? <p className="fbb-profession">Top Rated in {addr}</p> : <p className="fbb-profession">Featured Business</p>}
                  <div className="fbb-rating">
                    <StarRating rating={biz.rating} />
                    <span className="fbb-rating-text">{biz.rating ? biz.rating.toFixed(1) : "New"}</span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedBusinessBanner;